import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import { getErrorMessage } from "@fitness-app/utils";

import { backgroundTasksApi, supabase } from "~/store/initializeStore";

export type MusicVibe = "calm" | "energetic" | "story";

export interface MusicTrack {
  key: string;
  title: string;
  vibe: MusicVibe;
  startMs: number | null;
  addedAt: string;
  addedBy: string;
}

export const STUDIO_MUSIC_QUERY_KEY = ["studio", "music"];
const BUCKET = "studio-production";

export const useStudioMusic = () =>
  useQuery(STUDIO_MUSIC_QUERY_KEY, {
    queryFn: async () => {
      const response = await backgroundTasksApi.get<{ tracks: MusicTrack[] }>("/api/trainers/ai/studio-music");
      return response.data.tracks;
    },
  });

/**
 * Upload = signed URL from the API (service role) + direct PUT to the private bucket +
 * registration in the index. The file never passes through client-web.
 */
export const useUploadMusicTrack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, vibe, title }: { file: File; vibe: MusicVibe; title: string }) => {
      const { data: upload } = await backgroundTasksApi.post<{ key: string; token: string }>(
        "/api/trainers/ai/studio-music",
        { action: "upload-url", fileName: file.name, vibe },
      );
      const { error } = await supabase.storage.from(BUCKET).uploadToSignedUrl(upload.key, upload.token, file, {
        contentType: file.type || "audio/mpeg",
      });
      if (error) throw new Error(error.message);
      const { data } = await backgroundTasksApi.post<{ tracks: MusicTrack[] }>("/api/trainers/ai/studio-music", {
        action: "register",
        key: upload.key,
        title,
        vibe,
      });
      return data.tracks;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(STUDIO_MUSIC_QUERY_KEY);
      void message.success("Utwór dodany do biblioteki podkładów.");
    },
    onError: (error) => {
      void message.error(`Nie udało się dodać utworu: ${getErrorMessage(error)}`);
    },
  });
};

export const useRemoveMusicTrack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      const { data } = await backgroundTasksApi.delete<{ tracks: MusicTrack[] }>("/api/trainers/ai/studio-music", {
        params: { key },
      });
      return data.tracks;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(STUDIO_MUSIC_QUERY_KEY);
    },
    onError: (error) => {
      void message.error(`Nie udało się usunąć utworu: ${getErrorMessage(error)}`);
    },
  });
};
